import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { ServiceDetailPageContainer } from './ServiceDetailPage';
import { FaPaperPlane } from 'react-icons/fa';
import { motion } from 'framer-motion';
import emailjs from '@emailjs/browser';

const ServiceImage = styled.img`
  max-width: 200px; /* Smaller size */
  height: auto;
  border-radius: 0; /* Remove border-radius */
  margin: 0.5rem 0 1rem 2rem; /* Adjusted margin for text wrapping */
  box-shadow: none; /* Remove box-shadow */
  float: right; /* Allow text to wrap around */

  @media (max-width: 768px) {
    float: none;
    margin: 1rem auto;
    max-width: 80%;
    display: block;
  }
`;

const ContactSection = styled.section`
  padding: 4rem 2rem;
  background: linear-gradient(45deg, #00B4DB, #007bff);
  text-align: center;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 250px;
  color: white;
  position: relative;
  overflow: hidden;
  margin-top: 8rem;
  border-radius: 20px;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 30%;
    height: 100%;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.15), transparent);
    transform: skewX(-20deg) translateX(30%);
    transform-origin: top right;
    pointer-events: none;
    z-index: 0;
  }
`;

const ContactContent = styled.div`
  position: relative;
  z-index: 1;
  max-width: 600px;
  width: 100%;
`;

export const ContactTitle = styled.h2`
  color: #fff !important;
  margin-top: 0 !important;
`;

const ContactForm = styled.form`
  display: flex;
  gap: 1rem;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
`;

const ContactInput = styled.input`
  padding: 1rem 1.5rem;
  border: none;
  border-radius: 10px;
  font-size: 1rem;
  flex: 1;
  min-width: 200px;
  background: rgba(255, 255, 255, 0.9);
  color: #1f2937;

  &::placeholder {
    color: #6b7280;
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.5);
  }
`;

const SubmitButton = styled.button`
  background: #00B4DB;
  color: white;
  border: none;
  padding: 1rem 1.5rem;
  font-size: 1.2rem;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 180, 219, 0.2);

  &:hover {
    background: #007bff;
    transform: scale(1.02);
    box-shadow: 0 4px 12px rgba(0, 180, 219, 0.3);
  }

  svg {
    margin: 0;
  }
`;

function CorporateMail() {
  const [formData, setFormData] = useState({
    name: '',
    phone: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'phone') {
      // Only allow numbers and limit to exactly 11 digits
      const numbersOnly = value.replace(/[^\d]/g, '');
      if (numbersOnly.length <= 11) {
        setFormData({
          ...formData,
          [name]: numbersOnly
        });
      }
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.phone.length !== 11) {
      alert('Telefon numarası 11 haneli olmalıdır.');
      return;
    }

    try {
      const templateParams = {
        name: formData.name,
        phone: formData.phone,
        time: new Date().toLocaleString('tr-TR')
      };

      await emailjs.send(
        'service_771tgg7',
        'template_w676du8',
        templateParams,
        'xjG6_a9zdp6ZU1zHp'
      );

      alert('Başarılı');
      setFormData({ name: '', phone: '' });
    } catch (error) {
      alert('Bir hata oluştu');
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <ServiceDetailPageContainer>
      <h1>Kurumsal Mail Hizmeti</h1>
      <p>Profesyonel ve güvenli e-posta çözümleriyle kurumsal iletişiminizi sorunsuz hale getiriyoruz. İşletmenizin güvenliğini ve verimliliğini artıran özel mail altyapıları sunuyoruz.</p>

      <ServiceImage src="/media/demirer-ajans-4.png" alt="Kurumsal Mail Görseli 1" />

      <h2>Neden Kurumsal Mail Hizmeti Önemlidir?</h2>
      <p>Günümüz iş dünyasında e-posta, profesyonel iletişimin temelini oluşturur. Kurumsal bir e-posta adresi, işletmenizin güvenilirliğini artırırken, potansiyel müşteriler ve iş ortakları üzerinde olumlu bir ilk izlenim bırakır. Güvenli ve kesintisiz mail hizmetleri, işletmenizin operasyonel verimliliği için kritik öneme sahiptir.</p>

      <ServiceImage src="/media/demirer-ajans-5.png" alt="Kurumsal Mail Görseli 2" />

      <h3>Hizmetlerimiz Neleri Kapsar?</h3>
      <ul>
        <li><b>Profesyonel E-posta Adresleri:</b> Alan adınızla uyumlu (örn: bilgi@sirketiniz.com) profesyonel e-posta adresleri oluşturma.</li>
        <li><b>Geniş Depolama Alanı:</b> İhtiyaçlarınıza uygun geniş e-posta depolama çözümleri.</li>
        <li><b>Gelişmiş Güvenlik:</b> Spam ve virüs koruması, SSL şifreleme ve gelişmiş güvenlik önlemleri ile e-posta güvenliğinizi sağlama.</li>
        <li><b>Mobil ve Masaüstü Uyumluluk:</b> E-postalarınızın her yerden, tüm cihazlardan (bilgisayar, telefon, tablet) sorunsuz erişim.</li>
        <li><b>Kolay Yönetim Paneli:</b> E-posta hesaplarınızı kolayca yönetmenizi sağlayan kullanıcı dostu arayüz.</li>
        <li><b>Teknik Destek:</b> Her türlü sorunuz ve teknik ihtiyacınız için hızlı ve etkili destek.</li>
        <li><b>Otomatik Yedekleme:</b> E-postalarınızın düzenli olarak yedeklenmesi ile veri kaybı riskini minimize etme.</li>
      </ul>

      <h2>Kurumsal İletişiminizi Nasıl Güçlendiriyoruz?</h2>
      <ServiceImage src="/media/demirer-ajans-6.png" alt="Kurumsal Mail Görseli 3" />
      <p>İşletmenizin ölçeği ve ihtiyaçları ne olursa olsun, size özel kurumsal mail çözümleri sunuyoruz. İletişim altyapınızı güçlendirerek, iç ve dış yazışmalarınızın daha güvenli, düzenli ve verimli olmasını sağlıyoruz. Böylece siz de işinize odaklanabilir, operasyonel yükü bize bırakabilirsiniz.</p>
      <p>Profesyonel imajınızı güçlendirmek ve kurumsal iletişiminizi sorunsuz hale getirmek için bize güvenebilirsiniz.</p>

      <h2>Kurumsal Mail Hizmetlerimiz</h2>
      <p>Kurumsal mail hizmetlerimiz, işletmenizin profesyonel iletişimini güçlendirmek için tasarlanmıştır. Uzman ekibimiz, mail sunucusu kurulumu, yönetimi ve güvenliği konularında profesyonel çözümler sunar.</p>
      <p>Güvenli ve kesintisiz mail hizmetiyle, işletmenizin iletişimini güçlendiriyor ve verimliliğini artırıyoruz. İşletmenizin profesyonel iletişimini güçlendirmek için bize güvenebilirsiniz.</p>

      <ContactSection>
        <ContactContent>
          <ContactTitle>Sizi Arayalım</ContactTitle>
          <ContactForm onSubmit={handleSubmit}>
            <ContactInput 
              type="text" 
              name="name"
              placeholder="Adınız"
              value={formData.name}
              onChange={handleInputChange}
              required 
            />
            <ContactInput 
              type="tel" 
              name="phone"
              placeholder="Telefon Numaranız (11 Haneli)"
              value={formData.phone}
              onChange={handleInputChange}
              pattern="[0-9]*"
              inputMode="numeric"
              maxLength="11"
              minLength="11"
              required 
            />
            <SubmitButton
              type="submit"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <FaPaperPlane />
            </SubmitButton>
          </ContactForm>
        </ContactContent>
      </ContactSection>
    </ServiceDetailPageContainer>
  );
}

export default CorporateMail; 